// Used by: Quote, PortalQuoteOptions
import React, { forwardRef, useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import GetClassName from "../../utilities/getClassName";
import OverlayFade from "../ui/OverlayFade";
import AircraftCardImages from "./AircraftCardImages";
import { isFloatingFleet } from "../../utilities/aircraft";

import Checkbox from "../ui/Checkbox";
import AircraftCardExpandedContent from "./AircraftCardExpandedContent";
import AircraftCardActions from "./AircraftCardActions";
import AircraftCardDetails from "./AircraftCardDetails";
import chevronDownDarkIcon from "../../assets/icons/chevron-down-dark.svg";
import chevronUpGreyIcon from "../../assets/icons/chevron-up-grey.svg";

const AircraftCard = forwardRef(
    (
        {
            id,
            legId,
            recommended,
            category,
            aircraftName,
            photoData,
            seats,
            yom,
            refurbishedYear,
            cateringOption,
            luggage,
            toilet,
            ownerApproval,
            wifiOption,
            price,
            currency,
            operatorCurrency,
            expandedId,
            expandedHandler,
            exitAircraft,
            enterAircraft,
            sharedPage,
            approvalRequested,
            thumbsStatus,
            updateThumbsStatus,
            tailNumber,
            showCheckbox,
            compareSelectionHandler,
            compareSelected,
            isPortalPage,
            handlePortalBooking,
            showMovida,
            aircraftData,
            tripInfo,
            portalOption,
        },
        ref
    ) => {
        const [messages, setMessages] = useState([]);

        const updateExpandedStatus = () => {
            expandedHandler(id);
        };

        const showPriceInfoHandler = () => {
            setMessages((oldState) => {
                return [
                    {
                        id: oldState.length === 0 ? 0 : oldState[0]["id"] + 1,
                        text: `Price shown in ${operatorCurrency} is the original price provided by the Operator`,
                    },
                ];
            });
        };

        const floatingFleet = isFloatingFleet(aircraftData ?? { tailNumber });
        const isExpanded = expandedId instanceof Set ? expandedId.has(tailNumber) : expandedId === tailNumber;
        const isDemoExpanded = isExpanded;
        const actionsColumnRef = useRef(null);
        const cardRef = useRef(null);
        const [showFixedMinimal, setShowFixedMinimal] = useState(false);
        const [actionsOutOfView, setActionsOutOfView] = useState(false);
        const [cardInView, setCardInView] = useState(true);
        const [fixedRight, setFixedRight] = useState(20);

        const setRefs = useCallback(
            (node) => {
                cardRef.current = node;
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
            },
            [ref]
        );

        useEffect(() => {
            if (!isDemoExpanded) {
                setShowFixedMinimal(false);
                setActionsOutOfView(false);
                return;
            }
            if (isPortalPage) {
                setActionsOutOfView(false);
                return;
            }
            if (!actionsColumnRef.current) return;

            const updateRight = (rect) => {
                if (!rect) return;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const right = Math.max(viewportWidth - rect.right - 15, 16);
                setFixedRight(right);
            };

            const observer = new IntersectionObserver(
                ([entry]) => {
                    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                    const isDesktop = viewportWidth >= 1025;
                    if (!isDesktop) {
                        setActionsOutOfView(false);
                        setShowFixedMinimal(false);
                        return;
                    }
                    setActionsOutOfView(!entry.isIntersecting);
                    updateRight(entry.boundingClientRect);
                },
                {
                    threshold: 1,
                }
            );

            observer.observe(actionsColumnRef.current);
            return () => observer.disconnect();
        }, [isDemoExpanded, isPortalPage]);

        useEffect(() => {
            if (!isDemoExpanded || !cardRef.current || isPortalPage) return;

            const handleScroll = () => {
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const isDesktop = viewportWidth >= 1025;

                if (!isDesktop) {
                    setCardInView(false);
                    setShowFixedMinimal(false);
                    return;
                }

                const slideshowModal = document.querySelector(".modal--floorplan-slideshow");
                if (slideshowModal) {
                    setCardInView(false);
                    return;
                }

                const cardElement = cardRef.current;
                const headerOffset = 60;
                const collapseBtn = cardElement.querySelector(".aircraft-card-extra-content__collapse-btn");
                const rect = cardElement.getBoundingClientRect();

                const fixedTop = 60;
                const fixedHeightApprox = 260;
                const fixedBottom = fixedTop + fixedHeightApprox;

                let canShow = rect.top < headerOffset;

                if (collapseBtn) {
                    const collapseRect = collapseBtn.getBoundingClientRect();
                    if (collapseRect.top <= fixedBottom) {
                        canShow = false;
                    }
                }

                setCardInView(canShow);
            };

            handleScroll();
            window.addEventListener("scroll", handleScroll);
            window.addEventListener("resize", handleScroll);

            return () => {
                window.removeEventListener("scroll", handleScroll);
                window.removeEventListener("resize", handleScroll);
            };
        }, [isDemoExpanded, isPortalPage]);

        useEffect(() => {
            if (!isDemoExpanded || !actionsColumnRef.current || isPortalPage) return;

            const update = () => {
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const isDesktop = viewportWidth >= 1025;
                if (!isDesktop) {
                    setShowFixedMinimal(false);
                    return;
                }

                const rect = actionsColumnRef.current.getBoundingClientRect();
                const right = Math.max(viewportWidth - rect.right + 10, 16);
                setFixedRight(right);
            };

            update();
            window.addEventListener("resize", update);
            return () => window.removeEventListener("resize", update);
        }, [isDemoExpanded, isPortalPage]);

        useEffect(() => {
            setShowFixedMinimal(isDemoExpanded && actionsOutOfView && cardInView && !isPortalPage);
        }, [isDemoExpanded, actionsOutOfView, cardInView, isPortalPage]);

        return (
            <>
                <div
                    className={GetClassName([
                        "aircraft-card",
                        isDemoExpanded && "aircraft-card--demo-expanded",
                        portalOption?.isHidden && "aircraft-card--hidden",
                    ])}
                    ref={setRefs}
                >
                    <div
                        className={GetClassName([
                            "aircraft-card__content",
                            enterAircraft && "aircraft-card__content--enter",
                            exitAircraft && "aircraft-card__content--exit",
                        ])}
                    >
                        <div className="aircraft-card__main">
                            <AircraftCardImages
                                photoData={photoData}
                                messages={messages}
                                onClearMessages={() => setMessages([])}
                            />

                            <AircraftCardDetails
                                aircraftName={aircraftName}
                                recommended={recommended}
                                category={category}
                                floatingFleet={floatingFleet}
                                yom={yom}
                                refurbishedYear={refurbishedYear}
                                seats={seats}
                                luggage={luggage}
                                cateringOption={cateringOption}
                                toilet={toilet}
                                ownerApproval={ownerApproval}
                                wifiOption={wifiOption}
                                tailNumber={tailNumber}
                            />

                            <div ref={actionsColumnRef}>
                                <AircraftCardActions
                                    price={price}
                                    operatorCurrency={operatorCurrency}
                                    currency={currency}
                                    isPortalPage={isPortalPage}
                                    sharedPage={sharedPage}
                                    approvalRequested={approvalRequested}
                                    thumbsStatus={thumbsStatus}
                                    updateThumbsStatus={updateThumbsStatus}
                                    showMovida={showMovida}
                                    legId={legId}
                                    aircraftId={id}
                                    tailNumber={tailNumber}
                                    handlePortalBooking={handlePortalBooking}
                                    onInfoClick={showPriceInfoHandler}
                                    expanded={isExpanded}
                                    minimal={false}
                                    onFlightDetailsClick={updateExpandedStatus}
                                    isExpanded={isExpanded}
                                    portalOption={portalOption}
                                />
                            </div>
                        </div>

                        <div className="aircraft-card__flight-details">
                            <button className="aircraft-card__flight-details-toggle" onClick={updateExpandedStatus}>
                                <span className="aircraft-card__flight-details-label">Flight Details</span>
                                <img
                                    src={isExpanded ? chevronUpGreyIcon : chevronDownDarkIcon}
                                    alt=""
                                    className="aircraft-card__flight-details-chevron"
                                />
                            </button>
                        </div>

                        <div className="aircraft-card__footer">
                            {aircraftData && (
                                <AircraftCardExpandedContent
                                    expanded={
                                        expandedId instanceof Set
                                            ? expandedId.has(tailNumber)
                                            : expandedId === tailNumber
                                    }
                                    aircraftData={aircraftData}
                                    sharedPage={sharedPage}
                                    legId={legId}
                                    tripInfo={tripInfo}
                                    showMovida={showMovida}
                                    onCollapse={updateExpandedStatus}
                                />
                            )}
                        </div>
                    </div>

                    {isDemoExpanded &&
                        showFixedMinimal &&
                        !isPortalPage &&
                        createPortal(
                            <div className="aircraft-card__actions-fixed-minimal" style={{ right: fixedRight }}>
                                <AircraftCardActions
                                    price={price}
                                    operatorCurrency={operatorCurrency}
                                    currency={currency}
                                    isPortalPage={isPortalPage}
                                    sharedPage={sharedPage}
                                    approvalRequested={approvalRequested}
                                    thumbsStatus={thumbsStatus}
                                    updateThumbsStatus={updateThumbsStatus}
                                    showMovida={showMovida}
                                    legId={legId}
                                    aircraftId={id}
                                    tailNumber={tailNumber}
                                    handlePortalBooking={handlePortalBooking}
                                    onInfoClick={showPriceInfoHandler}
                                    expanded={true}
                                    minimal={true}
                                />
                            </div>,
                            document.body
                        )}

                    <OverlayFade spinner={exitAircraft || enterAircraft} show={exitAircraft} />
                    <OverlayFade show={showCheckbox} opacityPercentage={0.1} />

                    {showCheckbox && (
                        <div className="aircraft-card__checkbox-wrapper">
                            <Checkbox
                                defaultValue={compareSelected}
                                classNames={["checkbox--aircraft-compare"]}
                                tickDark
                                value={(value) => compareSelectionHandler(value)}
                            />
                        </div>
                    )}
                </div>
            </>
        );
    }
);

export default AircraftCard;
